import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, MapPin, Gauge, Clock, Fuel, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient } from '@/lib/api';

/**
 * A truck's telemetry, opened beside the list.
 *
 * Everything here is measured. What is deliberately absent is a tank gauge:
 * these trackers report no fuel level on any of the 43 units, so a dial would be
 * a picture of nothing. Fuel is stated as litres the cards paid for, and
 * labelled as such, because that is the number that exists.
 */

interface Condition {
  vehicle: Record<string, unknown>;
  tracker: Record<string, unknown> | null;
  fuel: { liters: string; sar: string; fills: number; source: string };
  today: { firstMove: string | null; lastMove: string | null; hours: { hour: number; moving: number }[] };
  daily: { day: string; km: number }[];
}

const n0 = (v: unknown) =>
  (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const clock = (s: string | null) =>
  (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' }) : '—');

export function VehicleTelemetryDrawer({
  vehicleId, plate, onClose,
}: {
  vehicleId: string;
  plate?: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicle-condition', vehicleId],
    queryFn: async () => (await apiClient.get<Condition>(`/api/v1/vehicle-condition/${vehicleId}`)).data,
  });

  const v = data?.vehicle ?? {};
  const t = data?.tracker ?? null;
  const movingHours = new Set((data?.today.hours ?? []).filter((h) => h.moving > 0).map((h) => h.hour));
  const maxKm = Math.max(1, ...(data?.daily ?? []).map((d) => d.km));

  const Stat = ({ icon: Icon, value, unit, label }: {
    icon: typeof Gauge; value: string; unit?: string; label: string;
  }) => (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-mono">{value}</span>
        {unit && <span className="text-[11px] text-muted-foreground">{unit}</span>}
      </div>
      <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
    </div>
  );

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono">{plate ?? String(v.plate_en ?? v.code ?? '')}</span>
            {v.side ? (
              <span className="text-[11px] rounded-full border border-border px-2 py-0.5 font-normal">{String(v.side)}</span>
            ) : null}
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-600 dark:text-rose-400 mt-4">
            Telemetry could not be loaded for this truck.
          </div>
        )}

        {data && (
          <div className="space-y-4 mt-4 pb-6">
            {v.driver_name ? (
              <div className="text-sm text-muted-foreground">
                driver: <b className="text-foreground">{String(v.driver_name)}</b>
                {v.iqama_number ? ` · ${String(v.iqama_number)}` : ''}
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-2">
              <Stat icon={Gauge} value={n0(t?.odometer_km)} unit="KM" label="Total mileage" />
              <Stat icon={Clock} value={n0(t?.engine_hours)} unit="h" label="Engine hours" />
              <Stat icon={Gauge} value={n0(t?.km_today)} unit="KM" label="Today’s running" />
              <Stat icon={Fuel} value={n0(data.fuel.liters)} unit="L" label="Litres purchased" />
            </div>

            {/* Where it is, as coordinates and a map link. No street address,
                because nothing here reverse-geocodes and an invented address is
                worse than none. */}
            <div className="rounded-lg border border-border p-3 space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Live position</div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                {t?.lat != null && t?.lng != null ? (
                  <a className="font-mono underline" target="_blank" rel="noreferrer"
                     href={`https://maps.google.com/?q=${String(t.lat)},${String(t.lng)}`}>
                    {Number(t.lat).toFixed(5)}, {Number(t.lng).toFixed(5)}
                  </a>
                ) : <span className="text-muted-foreground">no position reported</span>}
              </div>
              {t?.region ? <div className="text-xs text-muted-foreground">{String(t.region).toUpperCase()}</div> : null}
            </div>

            {/* The day, hour by hour. */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Today</span>
                <span className="text-xs text-muted-foreground font-mono">
                  {clock(data.today.firstMove)} – {clock(data.today.lastMove)}
                </span>
              </div>
              <div className="flex gap-[2px]">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} title={`${String(h).padStart(2, '0')}:00`}
                    className={`h-5 flex-1 rounded-[2px] ${movingHours.has(h) ? 'bg-emerald-500' : 'bg-muted'}`} />
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Movement, not ignition — the tracker reports ignition only as it is now.
              </p>
            </div>

            {data.daily.length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Last 14 days</div>
                <div className="flex items-end gap-1 h-20">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex-1" title={`${d.day} — ${n0(d.km)} km`}>
                      <div className="w-full rounded-t bg-primary/70"
                        style={{ height: `${Math.max(2, Math.round((d.km / maxKm) * 100))}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground">
              Fuel is litres the cards paid for — {data.fuel.source}.
            </p>

            <Link to={`/dashboard/vehicle-condition?v=${vehicleId}`}
              className="inline-flex items-center gap-1.5 text-sm underline text-muted-foreground hover:text-foreground">
              Open the full page <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
